import React, { useEffect } from "react";
import samhealthLogo from "../assets/samhealth.png";
import {
  Shield,
  FileText,
  Users,
  Clock,
  CheckCircle,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  Heart,
  Stethoscope,
  Activity,
  HelpCircle,
  User,
  LogIn,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getDashboardRouteByRole } from "../utils/navigationUtils";
import useNavigationGuard from "../hooks/useNavigationGuard";

const HomePage = () => {
  const navigate = useNavigate();
  const { currentUser, userRole, loading } = useAuth();
  const { isGuarded } = useNavigationGuard();

  // Don't render the home page if user is authenticated (will be redirected by navigation guard)
  if (loading) {
    return null; // Loading state handled by navigation guard
  }

  if (isGuarded) {
    return null; // Will be redirected by navigation guard
  }
  return (
    <div className="min-h-screen bg-gradient-corporate">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img src={samhealthLogo} alt="SamHealth" className="h-12 w-auto object-contain" />
              <div>
                <h1 className="text-2xl font-display font-bold text-gray-900 tracking-tight">SamHealth</h1>
                <p className="text-xs font-medium text-gray-500 tracking-wide uppercase">Insurance Processing Platform</p>
              </div>
            </div>

            <nav className="hidden md:flex items-center space-x-8">
              <a
                href="#services"
                className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
              >
                Services
              </a>
              <a
                href="#about"
                className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
              >
                About
              </a>
              <a
                href="#contact"
                className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
              >
                Contact
              </a>
              <a
                href="#help"
                className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
              >
                Help
              </a>

              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    navigate("/login");
                  }}
                  className="flex items-center space-x-2 px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all"
                >
                  <LogIn className="h-4 w-4" />
                  <span>Login</span>
                </button>
                <button
                  onClick={() => {
                    navigate("/lead-registration");
                  }}
                  className="flex items-center space-x-2 px-4 py-2.5 text-sm font-medium bg-gradient-primary text-white rounded-lg hover:shadow-lg transition-all"
                >
                  <User className="h-4 w-4" />
                  <span>Register As Lead</span>
                </button>
                <button
                  onClick={() => {
                    navigate("/hospital-registration");
                  }}
                  className="flex items-center space-x-2 px-4 py-2.5 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 hover:shadow-md transition-all"
                >
                  <User className="h-4 w-4" />
                  <span>Register Hospital</span>
                </button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-5xl lg:text-6xl font-display font-bold text-gray-900 mb-6 leading-tight">
                Streamline Your
                <span className="block text-primary-600 mt-2">
                  Insurance Processing
                </span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-xl">
                Complete digital platform for healthcare insurance claim
                processing. From hospital registration to patient applications,
                medical verification, and reimbursement tracking - all in one
                secure platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="flex items-center justify-center space-x-2 px-8 py-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all shadow-corporate hover:shadow-corporate-lg font-medium">
                  <span>Get Started Today</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
                <button className="flex items-center justify-center space-x-2 px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium">
                  <span>Learn More</span>
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl p-6 shadow-card">
                <img
                  src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Healthcare professionals"
                  className="w-full h-80 object-cover rounded-lg"
                />
              </div>
              <div className="absolute -top-4 -right-4 bg-primary-600 p-4 rounded-xl shadow-lg">
                <Shield className="h-12 w-12 text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-display font-bold text-gray-900 mb-4">
              Our Services
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive insurance processing solutions designed for
              healthcare providers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-card hover:shadow-lg transition-all border border-gray-100 hover:border-primary-200">
              <div className="bg-primary-600 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                <Stethoscope className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                Hospital Registration
              </h4>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Seamless registration process for hospitals with document
                verification, credential generation, and compliance checking.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Medical establishment verification</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>NABH & Quality Council certification</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Facility and specialty verification</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-card hover:shadow-lg transition-all border border-gray-100 hover:border-primary-200">
              <div className="bg-primary-600 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                <FileText className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                Claims Processing
              </h4>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                End-to-end insurance claim processing with automated workflows,
                document management, and real-time tracking.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Digital document submission</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Medical & KYC verification</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Policy underwriting support</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-card hover:shadow-lg transition-all border border-gray-100 hover:border-primary-200">
              <div className="bg-primary-600 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                <Users className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                Multi-Role Dashboard
              </h4>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Specialized dashboards for hospitals, doctors, policy teams, and
                administrators with role-based access control.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Hospital management portal</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Doctor verification interface</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Admin oversight tools</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-card hover:shadow-lg transition-all border border-gray-100 hover:border-primary-200">
              <div className="bg-primary-600 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                <Clock className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                Real-Time Tracking
              </h4>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Live status updates, timeline visualization, and instant
                notifications for all stakeholders throughout the process.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Application status timeline</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Real-time chat system</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Automated notifications</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-card hover:shadow-lg transition-all border border-gray-100 hover:border-primary-200">
              <div className="bg-primary-600 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                <Activity className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                Reimbursement Management
              </h4>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Comprehensive reimbursement tracking with automated processing,
                financial reporting, and audit trails.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Automated reimbursement processing</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Financial reporting & analytics</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Compliance & audit support</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-card hover:shadow-lg transition-all border border-gray-100 hover:border-primary-200">
              <div className="bg-primary-600 w-14 h-14 rounded-lg flex items-center justify-center mb-6">
                <Shield className="h-7 w-7 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-3">
                Secure Document Management
              </h4>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                Enterprise-grade security for sensitive medical documents with
                encrypted storage and controlled access.
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Encrypted document storage</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>HIPAA compliant security</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-accent-600" />
                  <span>Access control & audit logs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Modern healthcare technology"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </div>
            <div>
              <h3 className="text-4xl font-bold text-gray-800 mb-6">
                Transforming Healthcare Insurance Processing
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                SamHealth is a comprehensive digital platform
                designed to revolutionize the way healthcare insurance claims
                are processed. We bridge the gap between hospitals, patients,
                doctors, and insurance teams through seamless technology.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="bg-teal-100 p-2 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      Streamlined Workflows
                    </h4>
                    <p className="text-gray-600">
                      Automated processes reduce manual work and processing time
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-teal-100 p-2 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      Enhanced Security
                    </h4>
                    <p className="text-gray-600">
                      Enterprise-grade security for sensitive medical data
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="bg-teal-100 p-2 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      Real-Time Collaboration
                    </h4>
                    <p className="text-gray-600">
                      Instant communication between all stakeholders
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-800 mb-4">
              Contact Us
            </h3>
            <p className="text-xl text-gray-600">
              Get in touch with our team for support and inquiries
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center border border-teal-100">
              <div className="bg-gradient-to-r from-teal-500 to-cyan-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-gray-800 mb-2">
                Phone Support
              </h4>
              <p className="text-gray-600 mb-4">
                Available 24/7 for urgent inquiries
              </p>
              <p className="text-teal-600 font-semibold">+91 70191 44084</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center border border-teal-100">
              <div className="bg-gradient-to-r from-teal-500 to-cyan-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-gray-800 mb-2">
                Email Support
              </h4>
              <p className="text-gray-600 mb-4">Send us your queries anytime</p>
              <p className="text-teal-600 font-semibold">support@samhealth.co.in</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg text-center border border-teal-100">
              <div className="bg-gradient-to-r from-teal-500 to-cyan-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPin className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-gray-800 mb-2">
                Office Location
              </h4>
              <p className="text-gray-600 mb-4">Visit our headquarters</p>
              <p className="text-teal-600 font-semibold">
                2nd Floor, No. 17, Old PID No. 14-2-17,<br />
                20th Main Road, 1st "R" Block, Rajajinagar,<br />
                Bangalore - 560010, Karnataka, India
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Help Centre Section */}
      <section id="help" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-800 mb-4">
              Help Centre
            </h3>
            <p className="text-xl text-gray-600">
              Find answers to common questions and get support
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow text-center border border-teal-100">
              <div className="bg-teal-100 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                <HelpCircle className="h-6 w-6 text-teal-600" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">FAQ</h4>
              <p className="text-sm text-gray-600">
                Common questions and answers
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow text-center border border-teal-100">
              <div className="bg-teal-100 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FileText className="h-6 w-6 text-teal-600" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">
                Documentation
              </h4>
              <p className="text-sm text-gray-600">Complete platform guides</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow text-center border border-teal-100">
              <div className="bg-teal-100 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6 text-teal-600" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Training</h4>
              <p className="text-sm text-gray-600">
                Platform training sessions
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow text-center border border-teal-100">
              <div className="bg-teal-100 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Phone className="h-6 w-6 text-teal-600" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Live Support</h4>
              <p className="text-sm text-gray-600">24/7 customer support</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-cyan-500">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Insurance Processing?
          </h3>
          <p className="text-xl text-teal-100 mb-8">
            Join hundreds of healthcare providers who trust SamHealth
            for their insurance processing needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-teal-600 rounded-xl hover:bg-gray-50 transition-colors font-semibold shadow-lg"
              onClick={() => {
                navigate("/hospital-registration");
              }}
            >
              <User className="h-5 w-5" />
              <span>Register Your Hospital</span>
            </button>
            <button
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-white text-teal-600 rounded-xl hover:bg-gray-50 transition-colors font-semibold shadow-lg"
              onClick={() => {
                navigate("/lead-registration");
              }}
            >
              <User className="h-5 w-5" />
              <span>Register As Lead</span>
            </button>
            <button className="flex items-center justify-center space-x-2 px-8 py-4 border-2 border-white text-white rounded-xl hover:bg-white hover:text-teal-600 transition-colors font-semibold">
              <span>Schedule a Demo</span>
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img src={samhealthLogo} alt="SamHealth" className="h-8 w-auto object-contain" />
                <div>
                  <h4 className="text-xl font-bold">SamHealth</h4>
                  <p className="text-sm text-gray-400">Healthcare Bridge</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Leading healthcare technology platform connecting patients, hospitals,
                and corporate partners through innovative solutions and personalized care.
              </p>
            </div>

            <div>
              <h5 className="font-semibold mb-4">Services</h5>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="#" className="hover:text-teal-400 transition-colors">
                    Hospital Registration
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-teal-400 transition-colors">
                    Claims Processing
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-teal-400 transition-colors">
                    Medical Verification
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-teal-400 transition-colors">
                    Reimbursement
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h5 className="font-semibold mb-4">Contact</h5>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-teal-400" />
                  <span>
                    2nd Floor, No. 17, Old PID No. 14-2-17,<br />
                    20th Main Road, 1st "R" Block, Rajajinagar,<br />
                    Bangalore - 560010, Karnataka, India
                  </span>
                </li>
                <li className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-teal-400" />
                  <a href="tel:+917019144084" className="hover:text-teal-300 transition-colors">+91 70191 44084</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-teal-400" />
                  <a href="mailto:support@samhealth.co.in" className="hover:text-teal-300 transition-colors">support@samhealth.co.in</a>
                </li>
              </ul>

              <div className="mt-4">
                <p className="text-sm font-semibold text-white mb-2">Follow Us</p>
                <div className="flex items-center space-x-4 text-gray-400">
                  <a href="#" aria-label="Facebook" className="hover:text-teal-300 transition-colors">Facebook</a>
                  <a href="#" aria-label="Twitter" className="hover:text-teal-300 transition-colors">Twitter</a>
                  <a href="#" aria-label="LinkedIn" className="hover:text-teal-300 transition-colors">LinkedIn</a>
                  <a href="#" aria-label="Instagram" className="hover:text-teal-300 transition-colors">Instagram</a>
                </div>
              </div>
            </div>

            <div>
              <h5 className="font-semibold mb-4">Legal</h5>
        <ul className="space-y-2 text-sm text-gray-400">
                <li>
          <a href="https://www.samhealth.co.in/privacypolicy" target="_blank" rel="noreferrer" className="hover:text-teal-400 transition-colors">Privacy Policy</a>
                </li>
                <li>
          <a href="https://www.samhealth.co.in/tandc" target="_blank" rel="noreferrer" className="hover:text-teal-400 transition-colors">Terms of Service</a>
                </li>
                <li>
          <a href="https://www.samhealth.co.in/privacypolicy#cookies" target="_blank" rel="noreferrer" className="hover:text-teal-400 transition-colors">Cookie Policy</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400 text-sm">
        © {new Date().getFullYear()} Samriddhi Health. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
